// src/components/ServerDataExtraction/ExtractionOptions.jsx
import React, { useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const ExtractionOptions = ({
  options = {
    serverInfo: true,
    motd: true,
    serverIcon: true,
    playerList: true,
    pluginList: true
  },
  onChange,
  onExtract,
  isLoading,
  disabled
}) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  const handleToggle = useCallback((key) => {
    if (disabled || isLoading) return;
    if (onChange) {
      onChange({ ...options, [key]: !options[key] });
    }
  }, [options, onChange, disabled, isLoading]);

  const items = [
    {
      key: 'serverInfo',
      title: isEn ? 'Server Information' : 'Информация о сервере',
      sub: isEn ? 'Protocol, version, latency, SRV host' : 'Протокол, версия, пинг, SRV хост'
    },
    {
      key: 'motd',
      title: 'MOTD',
      sub: isEn ? 'Raw JSON, Minecraft colors, clean text' : 'Сырой JSON, цвета Minecraft, чистый текст'
    },
    {
      key: 'serverIcon',
      title: isEn ? 'Server Icon' : 'Иконка сервера',
      sub: isEn ? 'Favicon 64x64 in Base64 / PNG' : 'Favicon 64x64 в Base64 / PNG'
    },
    {
      key: 'playerList',
      title: isEn ? 'Player List' : 'Список игроков',
      sub: isEn ? 'Online, slots limit, sample nicknames & UUIDs' : 'Онлайн, лимит, сэмпл ников и UUID'
    },
    {
      key: 'pluginList',
      title: isEn ? 'Plugin List' : 'Список плагинов',
      sub: isEn ? 'Bukkit/Paper plugins detection' : 'Обнаружение Bukkit/Paper плагинов'
    },
    {
      key: 'dnsHistory',
      title: isEn ? 'DNS & IP History' : 'История DNS и IP-записей',
      sub: isEn ? 'Archived A/AAAA records & past hostings' : 'Архив прошлых IP, смены хостингов, исторические A/SRV',
      badge: 'OSINT'
    },
    {
      key: 'subdomainScan',
      title: isEn ? 'Subdomains & Nodes' : 'Поиск поддоменов и узлов',
      sub: isEn ? 'Discovery of hidden nodes (bungee, auth, dev, rcon)' : 'Поиск скрытых узлов (bungee, auth, dev, rcon, proxy)',
      badge: 'RECON'
    },
    {
      key: 'wafDetection',
      title: isEn ? 'Anti-DDoS & Hosting Fingerprint' : 'Определение Anti-DDoS и хостинга',
      sub: isEn ? 'StormWall, Cloudflare, TCPShield, ASN detection' : 'Детект защиты: StormWall, Cloudflare, TCPShield, ASN',
      badge: 'SHIELD'
    },
    {
      key: 'portScan',
      title: isEn ? 'Service Ports Recon' : 'Аудит сервисных портов',
      sub: isEn ? 'Audit open ports (RCON 25575, Votifier 8192, Dynmap)' : 'Сканирование портов (RCON 25575, Votifier 8192, Dynmap)',
      badge: 'PORTS'
    },
    {
      key: 'cveAudit',
      title: isEn ? 'Known Vulnerabilities & CVE' : 'Аудит уязвимостей (CVE)',
      sub: isEn ? 'Kernel CVE matching (Log4Shell, Netty, Crash packets)' : 'Сверка ядра с базой эксплойтов (Log4Shell, Netty, Crash-пакеты)',
      badge: 'VULN'
    },
    {
      key: 'modsManifest',
      title: isEn ? 'Forge / Fabric Mods' : 'Манифест Forge / Fabric модов',
      sub: isEn ? 'Extract installed server mods and versions via FML handshake' : 'Извлечение списка модов и библиотек через FML handshake',
      badge: 'MODS'
    },
    {
      key: 'geoDatacenter',
      title: isEn ? 'Geolocation & Datacenter' : 'Геолокация и дата-центр',
      sub: isEn ? 'Country, city, hosting datacenter, and latency (RU/EU/US)' : 'Страна, город, дата-центр хостинга и замер задержки (RU/EU/US)',
      badge: 'GEO'
    }
  ];

  return (
    <div className="glass-card">
      <div className="card-header-line">
        <div className="card-title-wrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          <h3 className="card-title">
            {isEn ? 'Extraction Parameters' : 'Параметры извлечения'}
          </h3>
        </div>
      </div>

      {/* Список параметров */}
      <div className="extraction-options-list mb-6">
        {items.map(item => {
          const isChecked = !!options[item.key];
          return (
            <label
              key={item.key}
              className={`extract-item-label ${isChecked ? 'checked' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                handleToggle(item.key);
              }}
            >
              <div className="extract-item-left">
                <div className="custom-checkbox">
                  <svg viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <div className="extract-item-info">
                  <div className="flex items-center gap-2">
                    <span className="extract-item-name">{item.title}</span>
                    {item.badge && (
                      <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-teal-500/10 text-teal-400 border border-teal-500/20 uppercase tracking-wider">
                        {item.badge}
                      </span>
                    )}
                  </div>
                  <span className="extract-item-sub">{item.sub}</span>
                </div>
              </div>
            </label>
          );
        })}
        <div className="h-2.5 shrink-0 pointer-events-none" aria-hidden="true" />
      </div>

      {/* КНОПКА ЗАПУСКА */}
      <button
        type="button"
        className="action-btn-main"
        onClick={onExtract}
        disabled={isLoading || disabled}
      >
        {isLoading ? (
          <>
            <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin shrink-0" />
            <span>{isEn ? 'Extracting Data...' : 'Выкачивание данных...'}</span>
          </>
        ) : (
          <>
            <svg viewBox="0 0 24 24">
              <polygon points="5 3 19 12 5 21 5 3" fill="currentColor" />
            </svg>
            <span>{isEn ? 'Start Data Extraction' : 'Запустить извлечение данных'}</span>
          </>
        )}
      </button>
    </div>
  );
};

export default ExtractionOptions;